import * as moment from 'moment';
import React, { useMemo, useImperativeHandle, forwardRef, useRef, useContext } from 'react';
import { IPatientIntakeListItem } from '@nkr/models/index';
import { Utility } from '@nkr/services/utility';
import { ColDef, GridReadyEvent, GridSizeChangedEvent, ICellRendererParams, IHeaderParams, RowDataChangedEvent, ValueGetterParams, GridApi, CellClickedEvent, SortChangedEvent } from 'ag-grid-community';
import { AgGridReact } from 'ag-grid-react';
import { Glyphicon, Label } from 'react-bootstrap';
import { PrimaryGoalType } from '@nkr/enums/index';
import { PatientIntakeTabs } from './nav';
import { PatientIntakeContext } from './context';
import SortableHeaderComponent from '@nkr/views/admin/center/list/common/sortable-header';
import RegistrationStatusLabel from './registration-status-label';
import ReferralStatusLabel from './referral-status-label';
import PrimaryGoalLabel from './primary-goal-label';
import WorkflowActionsDropdown from './workflow-actions-dropdown';

export interface CommonListRef {
    getGridApi: () => GridApi | null;
}

interface IProps {
    data: IPatientIntakeListItem[],
    isMicrositeCenter: boolean,
    isAdmin: boolean
}

const CommonList = forwardRef<CommonListRef, IProps>((props, ref) => {
    const context = useContext(PatientIntakeContext);

    const gridApiRef = useRef<GridApi | null>(null);

    useImperativeHandle(ref, () => ({
        getGridApi: () => gridApiRef.current,
    }));

    const onGridReady = (e: GridReadyEvent) => {
        gridApiRef.current = e.api;
        e.api.sizeColumnsToFit();
    };

    const onGridSizeChanged = (e: GridSizeChangedEvent) => {
        e.api.sizeColumnsToFit();
    };

    const onRowDataChanged = (e: RowDataChangedEvent) => {
        e.api.sizeColumnsToFit();
    };

    const onCellClicked = (e: CellClickedEvent) => {
        let colId = e.column.getColId();

        if (colId !== 'actions' && colId != 'recipientAlias' && colId != 'micrositeRecipientId') {
            let data = e.data as IPatientIntakeListItem;
            context.onView(data.medicalRecordId);
        }
    };

    const onSortChanged = (e: SortChangedEvent) => {
        e.api.refreshCells();
    };

    const columns: ColDef[] = [
        {
            colId: '1',
            field: 'rank',
            headerName: 'R#',
            sortable: false,
            width: 60,
            pinned: 'left',
            // pinnedRowCellRendererFramework: () => <div></div>,
            headerComponentFramework: (params: IHeaderParams) => <SortableHeaderComponent {...params}>R#</SortableHeaderComponent>,
            valueGetter: (props: ValueGetterParams) => props.node.rowIndex + 1,
        },
        {
            colId: 'source',
            field: 'source',
            headerName: 'Source',
            sortable: true,
            width: 120,
            headerComponentFramework: (params: IHeaderParams) => <SortableHeaderComponent {...params}>Source</SortableHeaderComponent>,
            cellRendererFramework: (params: ICellRendererParams) => {
                return params.value ? <>{params.value}</> : <Label>NOT SPECIFIED</Label>
            }
        },
        {
            colId: 'centerName',
            field: 'centerName',
            hide: context.isAdminView == false,
            headerName: 'Center',
            sortable: true,
            width: 150,
            headerComponentFramework: (params: IHeaderParams) => <SortableHeaderComponent {...params}>Center</SortableHeaderComponent>,
            cellRendererFramework: (params: ICellRendererParams) => {
                let data = params.data as IPatientIntakeListItem;

                return data.centerName != '' ? <>{data.centerName}</> : <Label>NO CENTER</Label>
            },
            comparator: Utility.nameComparator
        },
        {
            colId: 'firstName',
            field: 'firstName',
            headerName: 'First Name',
            sortable: true,
            comparator: Utility.nameComparator,
            width: 120,
            headerComponentFramework: (params: IHeaderParams) => <SortableHeaderComponent {...params}>First<br />Name</SortableHeaderComponent>,
        },
        {
            colId: 'lastName',
            field: 'lastName',
            headerName: 'Last Name',
            sortable: true,
            comparator: Utility.nameComparator,
            width: 120,
            headerComponentFramework: (params: IHeaderParams) => <SortableHeaderComponent {...params}>Last<br />Name</SortableHeaderComponent>,
        },
        {
            colId: 'email',
            field: 'email',
            headerName: 'Email',
            sortable: true,
            width: 200,
            headerComponentFramework: (params: IHeaderParams) => <SortableHeaderComponent {...params}>Email</SortableHeaderComponent>,
            cellRendererFramework: (params: ICellRendererParams) => {
                return params.value ? <>{params.value}</> : <Label>not specified</Label>
            }
        },
        {
            colId: 'dateOfBirth',
            field: 'dateOfBirth',
            headerName: 'Date of Birth',
            sortable: true,
            width: 110,
            headerComponentFramework: (params: IHeaderParams) => <SortableHeaderComponent {...params}>Date of Birth</SortableHeaderComponent>,
            comparator: Utility.dateComparator
        },
        {
            colId: 'registrationDate',
            field: 'registrationDate',
            headerName: 'Registration Date',
            sortable: true,
            width: 100,
            headerComponentFramework: (params: IHeaderParams) => <SortableHeaderComponent {...params}>Registration<br />Date</SortableHeaderComponent>,
            comparator: Utility.dateComparator
        },
        {
            colId: 'registrationStatus',
            field: 'registrationStatus',
            headerName: 'Registration Status',
            sortable: true,
            width: props.isAdmin ? 105 : 125,
            headerComponentFramework: (params: IHeaderParams) => <SortableHeaderComponent {...params}>Registration<br />Status</SortableHeaderComponent>,
            cellRendererParams: { isAdmin: props.isAdmin },
            cellRendererFramework: RegistrationStatusLabel
        },
        {
            colId: 'referralStatus',
            field: 'referralStatus',
            headerName: 'Referral Status',
            sortable: true,
            width: 130,
            headerComponentFramework: (params: IHeaderParams) => <SortableHeaderComponent {...params}>Referral<br />Status</SortableHeaderComponent>,
            cellRendererFramework: ReferralStatusLabel
        },
        {
            colId: 'coordinatorName',
            field: 'coordinatorName',
            headerName: 'Coordinator',
            sortable: true,
            width: 150,
            headerComponentFramework: (params: IHeaderParams) => <SortableHeaderComponent {...params}>Coordinator</SortableHeaderComponent>,
            cellRendererFramework: (params: ICellRendererParams) => {
                let data: IPatientIntakeListItem = params.data;
                return data.coordinatorName && data.transferRequest == false ? <>{params.value}</> : '--';
            }
        },
        {
            colId: 'followUpDate',
            field: 'followUpDate',
            headerName: 'Next Follow-Up',
            sortable: true,
            width: 130,
            headerComponentFramework: (params: IHeaderParams) => <SortableHeaderComponent {...params}>Next<br />Follow-Up</SortableHeaderComponent>,
            cellRendererFramework: (params: ICellRendererParams) => {
                let data = params.data as IPatientIntakeListItem;
                let nextFollowUpDate = moment(data.followUpDate, 'MM/DD/YYYY', true);

                if (!data.followUpDate) {
                    return '--';
                }

                if (nextFollowUpDate.isBefore(moment(), 'day')) {
                    return <Label bsStyle='danger'>{data.followUpDate}</Label>
                }

                if (nextFollowUpDate.isAfter(moment(), 'day')) {
                    return <Label bsStyle='success'>{data.followUpDate}</Label>
                }

                if (nextFollowUpDate.isSame(moment(), 'day')) {
                    return <Label bsStyle='warning'>{data.followUpDate}</Label>
                }

                return '--';
            },
            comparator: Utility.dateComparator
        },
        {
            colId: 'selectedGoal',
            field: 'selectedGoal',
            headerName: 'Goal',
            sortable: true,
            width: 140,
            headerComponentFramework: (params: IHeaderParams) => <SortableHeaderComponent {...params}>Primary<br />Goal</SortableHeaderComponent>,
            cellRendererFramework: (params: ICellRendererParams) => {
                let data = params.data as IPatientIntakeListItem;

                if (data?.selectedGoal === undefined || data?.selectedGoal === null) {
                    return 'N/A';
                }

                return <PrimaryGoalLabel {...params} />;
            },
            valueGetter: (params: ValueGetterParams) => {
                let data = params.data as IPatientIntakeListItem;

                return data?.selectedGoal;
            },
            valueFormatter: (params) => {
                let goal = params.value;

                if (goal === undefined || goal === null) return 'N/A';

                switch (goal) {
                    case PrimaryGoalType.TransferToMicrositeCenter: return 'MS';
                    case PrimaryGoalType.TransferToKidneyForLifeCenter: return 'KFL';
                    case PrimaryGoalType.TransferToImmunoFreeCenter: return 'Immuno Free';
                    case PrimaryGoalType.StandardVoucher: return 'Standard Voucher';
                    case PrimaryGoalType.FindCenter: return 'Find Center';
                    case PrimaryGoalType.UnderstandOptions: return 'Understand Options';
                    case PrimaryGoalType.Other: return 'Other';
                    default: return 'N/A';
                }
            }
        },
        {
            colId: 'recipientAlias',
            field: 'recipientAlias',
            headerName: 'Recipient',
            sortable: true,
            width: 120,
            hide: context.currentTab == PatientIntakeTabs.TO_DO || context.currentTab == PatientIntakeTabs.PENDING_ORDERS || context.currentTab == PatientIntakeTabs.PENDING_RECIPIENT_PROFILE,
            headerComponentFramework: (params: IHeaderParams) => <SortableHeaderComponent {...params}>Recipient</SortableHeaderComponent>,
            cellRendererFramework: (params: ICellRendererParams) => {
                let data = params.data as IPatientIntakeListItem;

                if (data.recipientAlias != '') {
                    if (data.recipientSameCenter) {
                        return <a href={`/center/${data.centerId}/recipient/${data.recipientId}/profile/general`} target='_blank'>{data.recipientAlias}</a>
                    } else {
                        return <div style={{ lineHeight: '10px', fontSize: 12 }}>{data.recipientAlias}<br />@ {data.recipientCenter}</div>
                    }
                }

                return null;
            }
        },
        {
            colId: 'recipientTransplantDate',
            field: 'recipientTransplantDate',
            headerName: "TXP'D",
            sortable: true,
            width: 120,
            comparator: Utility.dateComparator,
            hide: !context.isAdminView,
            headerComponentFramework: (params: IHeaderParams) => <SortableHeaderComponent {...params}>TXP'D</SortableHeaderComponent>,
            cellRendererFramework: (params: ICellRendererParams) => {
                let data = params.data as IPatientIntakeListItem;
                let val = data.recipientTransplantDate;

                return val ? <>{val}</> : '--';
            }
        },
        {
            colId: 'micrositeRecipientId',
            field: 'micrositeRecipientId',
            headerName: 'Microsite',
            sortable: true,
            width: 120,
            hide: context.currentTab == PatientIntakeTabs.TO_DO || context.currentTab == PatientIntakeTabs.PENDING_ORDERS || props.isMicrositeCenter == false,
            headerComponentFramework: (params: IHeaderParams) => <SortableHeaderComponent {...params}>Microsite</SortableHeaderComponent>,
            cellRendererFramework: (params: ICellRendererParams) => {
                let data = params.data as IPatientIntakeListItem;

                if (data.micrositeRecipientId > 0) {
                    if (data.micrositeSameCenter) {
                        return <a href={`/center/${data.centerId}/microsite/${data.micrositeRecipientId}/registration`} target='_blank'>Yes</a>
                    } else {
                        return <div style={{ lineHeight: '10px', fontSize: 12 }}>Yes @ {data.micrositeRecipientCenter}</div>
                    }
                }

                return <>No</>;
            }
        },
        {
            colId: 'transferRequest',
            field: 'transferRequest',
            headerName: 'Pending Transfer Request',
            width: 90,
            hide: true,
            headerComponentFramework: (params: IHeaderParams) => <SortableHeaderComponent {...params}>Pending<br />Transfer</SortableHeaderComponent>,
            cellRendererFramework: (params: ICellRendererParams) => {
                let data = params.data as IPatientIntakeListItem;

                if (data.transferRequest) {
                    return <Glyphicon glyph='ok' />
                }

                return null;
            }
        },
        {
            colId: 'actions',
            headerName: 'Actions',
            sortable: false,
            width: 100,
            suppressNavigable: true,
            headerComponentFramework: (params: IHeaderParams) => <SortableHeaderComponent {...params}>Actions</SortableHeaderComponent>,
            cellRendererFramework: (params: ICellRendererParams) => {
                let data = params.data as IPatientIntakeListItem;

                return (
                    <WorkflowActionsDropdown
                        hasAboResults={(data as any).hasAboResults || false}
                        hasPendingPreWorkupLabs={data.hasPendingPreWorkupLabs || false}
                        medicalRecordId={data.medicalRecordId}
                        patientScreenId={data.patientScreenId}
                        firstName={data.firstName}
                        lastName={data.lastName}
                        allowTransfer={data.patientScreenChoiceId > 0 && data.transferRequest == false}
                        isList={true}
                    />
                );
            }
        }
    ];

    const columnDefs = useMemo(() => columns, [context.currentTab, props.isMicrositeCenter]);
    const rowData = useMemo(() => props.data, [props.data]);

    return (
        <AgGridReact
            defaultColDef={{
                headerClass: 'list-header',
                cellClass: 'list-cell',
                sortable: true
            }}
            columnDefs={columnDefs}
            rowData={rowData}
            headerHeight={44}
            rowHeight={34}
            suppressContextMenu={true}
            suppressCellSelection={true}
            suppressRowClickSelection={true}
            onGridReady={onGridReady}
            onGridSizeChanged={onGridSizeChanged}
            onRowDataChanged={onRowDataChanged}
            onCellClicked={onCellClicked}
            onSortChanged={onSortChanged}
        />
    );
});

export default CommonList;